import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
  Button
} from "@mui/material";

import { BarChart } from "@mui/x-charts/BarChart";

export default function GiooPlotsDashboard({ weeklyGraph = [], monthlyGraph = [], totalUnits = 100, loading }) {
  const [tab, setTab] = useState("weekly");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // The requested brand color
  const BRAND_GREEN = "#92AF5D";
  const ACCENT_GOLD = "#E6B422"; // Keeping the gold for contrast

  const transformData = (data = [], type) =>
    data.map((item) => ({
      name: type === "weekly"
        ? (item.day ? item.day.slice(0, 3) : "N/A")
        : `${item.month ? item.month.slice(0, 3) : ""}`,
      booked: item.total || 0,
      available: totalUnits - (item.total || 0),
    }));

  const chartData = transformData(
    tab === "weekly" ? weeklyGraph : monthlyGraph,
    tab
  );

  const labels = chartData.map((d) => d.name);
  const bookedData = chartData.map((d) => d.booked);
  const availableData = chartData.map((d) => d.available);

  const maxTick = Math.ceil(totalUnits / 10) * 10;

  return (
    <Box maxWidth="1200px" mx="auto">
      <Card sx={{ borderRadius: 4, p: isMobile ? 1.5 : 3, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 0 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={2}
            mb={4}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: "#333" }}>
              Occupancy Overview
            </Typography>

            {/* Styled Toggle with Brand Green */}
            <Box display="flex" gap={0.5} bgcolor="#F4F6F2" p={0.5} borderRadius={2.5}>
              {["weekly", "monthly"].map((t) => (
                <Button
                  key={t}
                  onClick={() => setTab(t)}
                  size="small"
                  sx={{
                    px: 3,
                    bgcolor: tab === t ? BRAND_GREEN : "transparent",
                    color: tab === t ? "#fff" : "#777",
                    fontWeight: tab === t ? 600 : 500,
                    borderRadius: 2,
                    textTransform: "capitalize",
                    transition: "all 0.2s ease",
                    "&:hover": { 
                        bgcolor: tab === t ? BRAND_GREEN : "#E8EDDF",
                        color: tab === t ? "#fff" : BRAND_GREEN 
                    },
                  }}
                >
                  {t}
                </Button>
              ))}
            </Box>
          </Stack>

          <Box sx={{ width: "100%", height: isMobile ? 280 : 350 }}>
            {loading ? (
              <Typography align="center" sx={{ mt: 5 }}>Syncing data...</Typography>
            ) : chartData.length > 0 ? (
              <BarChart
                xAxis={[
                  {
                    data: labels,
                    scaleType: "band",
                  },
                ]}
                series={[
                  {
                    data: bookedData,
                    label: "Booked Units",
                    color: BRAND_GREEN,
                    valueFormatter: (value) => `${value} Units`,
                  },
                  {
                    data: availableData,
                    label: "Available Units",
                    color: ACCENT_GOLD,
                    valueFormatter: (value) => `${value} Units`,
                  },
                ]}
                height={isMobile ? 280 : 350}
                margin={{ top: 50, right: 20, left: 40, bottom: 30 }}
                slotProps={{
                  legend: {
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'middle' },
                    padding: 0,
                  },
                }}
              />
            ) : (
              <Typography align="center" sx={{ mt: 5 }} color="text.secondary">No data found</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
} 